/**
 * QC limits ("Reflight specifications") as data (TJR inventory 01 §4; MB decisions 13, 122, 123).
 *
 * TJR stores seven printed rules (R1–R7) and the hand-sample position tolerance (R8, not printed). The values and
 * enable flags of F26048 (`Report_Log_F26048.tjf` records 600.8–600.17, 610.0–610.1, 033, 034, 036, 664) are MB's
 * Benz example and Thomson Airborne's tenant defaults (decision 123). Each rule prints with the tenant's sentence
 * (`ctrdatfls.dat` 001, clause phrases `reflight.<key>`); `{limit}`, `{over}`, `{window}`, `{sensor}` are filled
 * here.
 */
import type { ValidationIssue } from './model.js';

export type QcRuleKey =
  | 'flight_path'
  | 'terrain_clearance'
  | 'mag_noise_4th_diff'
  | 'diurnal_nonlinear'
  | 'diurnal_chord'
  | 'mag_sample_interval'
  | 'th_hand_sample'
  | 'th_test_line'
  | 'ground_cal_position';

export type QcUnit = 'm' | 'nT' | '%';

export interface QcRule {
  readonly key: QcRuleKey;
  readonly enabled: boolean;
  /** The limit: ± metres, nT or %. */
  readonly limit: number;
  /** "for more than … metres" (flight path, terrain clearance, noise, sample interval). */
  readonly over_m: number | null;
  /** "in … minutes" (non-linear diurnal) or the chord length in minutes (diurnal chord). */
  readonly window_min: number | null;
  /** Which magnetometer the noise rule applies to: "tail" on the C208 (decision 123), or null for all. */
  readonly sensor: string | null;
}

export interface QcRuleDef {
  readonly key: QcRuleKey;
  readonly label: string;
  readonly unit: QcUnit;
  readonly over: boolean;
  readonly window: boolean;
  /** Printed in contract 2.12 (R8, the position tolerance, is not). */
  readonly printed: boolean;
  /** Decimal limits allowed (TJR: noise and sample interval only). */
  readonly decimal: boolean;
}

export const QC_RULE_DEFS: readonly QcRuleDef[] = [
  {
    key: 'flight_path',
    label: 'Flight path deviation',
    unit: 'm',
    over: true,
    window: false,
    printed: true,
    decimal: false,
  },
  {
    key: 'terrain_clearance',
    label: 'Terrain clearance deviation',
    unit: 'm',
    over: true,
    window: false,
    printed: true,
    decimal: false,
  },
  {
    key: 'mag_noise_4th_diff',
    label: 'Magnetometer noise (4th difference)',
    unit: 'nT',
    over: true,
    window: false,
    printed: true,
    decimal: true,
  },
  {
    key: 'diurnal_nonlinear',
    label: 'Non-linear diurnal variation',
    unit: 'nT',
    over: false,
    window: true,
    printed: true,
    decimal: false,
  },
  {
    key: 'diurnal_chord',
    label: 'Diurnal deviation from a chord',
    unit: 'nT',
    over: false,
    window: true,
    printed: true,
    decimal: false,
  },
  {
    key: 'mag_sample_interval',
    label: 'Magnetometer sample interval',
    unit: 'm',
    over: true,
    window: false,
    printed: true,
    decimal: true,
  },
  {
    key: 'th_hand_sample',
    label: 'Thorium hand sample change',
    unit: '%',
    over: false,
    window: false,
    printed: true,
    decimal: false,
  },
  {
    key: 'th_test_line',
    label: 'Thorium test line change',
    unit: '%',
    over: false,
    window: false,
    printed: true,
    decimal: false,
  },
  {
    key: 'ground_cal_position',
    label: 'Hand sample position',
    unit: 'm',
    over: false,
    window: false,
    printed: false,
    decimal: false,
  },
];

export const QC_RULE_KEYS: readonly QcRuleKey[] = QC_RULE_DEFS.map((d) => d.key);

const rule = (
  key: QcRuleKey,
  limit: number,
  over_m: number | null = null,
  window_min: number | null = null,
  sensor: string | null = null,
): QcRule => ({ key, enabled: true, limit, over_m, window_min, sensor });

/**
 * F26048 (MB's Benz example): 15 m over 1000 m, 10 m over 1000 m, ±0.1 nT over 1000 m, 10 nT in 10 min, 10 nT from a
 * 10-min chord, 4.5 m over 1000 m, 3 %, 7 %, position 5 m; all enabled (record 664 = -1 × 5).
 */
export const BENZ_F26048_QC_RULES: readonly QcRule[] = [
  rule('flight_path', 15, 1000),
  rule('terrain_clearance', 10, 1000),
  rule('mag_noise_4th_diff', 0.1, 1000),
  rule('diurnal_nonlinear', 10, null, 10),
  rule('diurnal_chord', 10, null, 10),
  rule('mag_sample_interval', 4.5, 1000),
  rule('th_hand_sample', 3),
  rule('th_test_line', 7),
  rule('ground_cal_position', 5),
];

/**
 * Whether an aircraft type names the Cessna 208 family: "Cessna 208", "Cessna 208B" (VH-JOZ in the F26048 data,
 * issue #13), "C208", "C208B", "Grand Caravan" or "Caravan".
 */
export function isCessna208(aircraftType: string): boolean {
  return /\b((c\s?|cessna\s+)208[a-z]?|grand caravan|caravan)\b/i.test(aircraftType);
}

/** The C208 has three magnetometers; noise applies to the tail sensor only (decision 123). */
export function qcDefaultsForAircraftType(base: readonly QcRule[], aircraftType: string): QcRule[] {
  const c208 = isCessna208(aircraftType);
  return base.map((r) => (r.key === 'mag_noise_4th_diff' && c208 ? { ...r, sensor: 'tail' } : { ...r }));
}

/** Rules in the canonical order with any missing key added, disabled. */
export function normaliseQcRules(rules: readonly QcRule[]): QcRule[] {
  return QC_RULE_DEFS.map((d) => {
    const found = rules.find((r) => r.key === d.key);
    return found
      ? { ...found }
      : { key: d.key, enabled: false, limit: 0, over_m: null, window_min: null, sensor: null };
  });
}

export function qcRule(rules: readonly QcRule[], key: QcRuleKey): QcRule | null {
  return rules.find((r) => r.key === key) ?? null;
}

/** Every enabled rule needs a limit above 0 and, where the sentence has one, a distance or a time above 0. */
export function validateQcRules(rules: readonly QcRule[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const d of QC_RULE_DEFS) {
    const r = qcRule(rules, d.key);
    if (!r || !r.enabled) continue;
    const field = `qc.${d.key}`;
    if (!(Number.isFinite(r.limit) && r.limit > 0)) {
      issues.push({ field, message: `${d.label}: enter a limit above 0 ${d.unit}.` });
    } else if (!d.decimal && !Number.isInteger(r.limit)) {
      issues.push({ field, message: `${d.label}: enter a whole number of ${d.unit}.` });
    }
    if (d.unit === '%' && r.limit > 100)
      issues.push({ field, message: `${d.label}: the limit cannot be over 100 %.` });
    if (d.over && !(r.over_m !== null && Number.isFinite(r.over_m) && r.over_m > 0)) {
      issues.push({ field, message: `${d.label}: enter the distance in metres.` });
    }
    if (d.window && !(r.window_min !== null && Number.isFinite(r.window_min) && r.window_min > 0)) {
      issues.push({ field, message: `${d.label}: enter the time in minutes.` });
    }
  }
  return issues;
}

/** "1,000" style: Australian thousands separator, no trailing zeros. */
export function formatNumber(n: number, maxDecimals = 2): string {
  return n.toLocaleString('en-AU', { maximumFractionDigits: maxDecimals });
}

/**
 * The printed sentence of one rule from the tenant's wording, e.g. "Flight path deviates by more than **+/- 15
 * metres for more than 1,000 metres** …". Placeholders: {limit} {over} {window} {sensor} (empty, or "tail " when
 * the rule names a sensor).
 */
export function reflightSentence(r: QcRule, wording: string): string {
  return wording
    .replaceAll('{limit}', formatNumber(r.limit, 3))
    .replaceAll('{over}', r.over_m === null ? '' : formatNumber(r.over_m))
    .replaceAll('{window}', r.window_min === null ? '' : formatNumber(r.window_min))
    .replaceAll('{sensor}', r.sensor ? `${r.sensor} ` : '');
}

/**
 * Printed reflight rules in contract order. Non-linear diurnal and the chord print as one TJR sentence (R4) when
 * both are enabled; the phrase `reflight.diurnal` holds that sentence, `reflight.diurnal_nonlinear` and
 * `reflight.diurnal_chord` the single halves.
 */
export function reflightSentences(
  rules: readonly QcRule[],
  phrases: Readonly<Record<string, string>>,
): string[] {
  const on = (k: QcRuleKey): QcRule | null => {
    const r = qcRule(rules, k);
    return r && r.enabled ? r : null;
  };
  const out: string[] = [];
  for (const d of QC_RULE_DEFS) {
    if (!d.printed) continue;
    const r = on(d.key);
    if (!r) continue;
    if (d.key === 'diurnal_nonlinear' || d.key === 'diurnal_chord') {
      const nl = on('diurnal_nonlinear');
      const ch = on('diurnal_chord');
      if (d.key === 'diurnal_chord' && nl) continue; // printed with the non-linear rule
      if (nl && ch && phrases['reflight.diurnal']) {
        out.push(
          phrases['reflight.diurnal']
            .replaceAll('{limit}', formatNumber(nl.limit))
            .replaceAll('{window}', formatNumber(nl.window_min ?? 0))
            .replaceAll('{chord_limit}', formatNumber(ch.limit))
            .replaceAll('{chord_window}', formatNumber(ch.window_min ?? 0)),
        );
        continue;
      }
    }
    const wording = phrases[`reflight.${d.key}`];
    if (wording) out.push(reflightSentence(r, wording));
  }
  return out;
}

/** The thorium and position tolerances Field reads from the project (`project.qc_hand_sample_pct` and so on). */
export function projectQcColumns(rules: readonly QcRule[]): {
  readonly qc_hand_sample_pct: number | null;
  readonly qc_test_line_pct: number | null;
} {
  const hs = qcRule(rules, 'th_hand_sample');
  const tl = qcRule(rules, 'th_test_line');
  return {
    qc_hand_sample_pct: hs && hs.enabled ? hs.limit : null,
    qc_test_line_pct: tl && tl.enabled ? tl.limit : null,
  };
}

/** Plain-words list of what changed between two rule sets (for the variation record). */
export function qcChanges(before: readonly QcRule[], after: readonly QcRule[]): string[] {
  const out: string[] = [];
  const show = (r: QcRule | null, d: QcRuleDef): string => {
    if (!r || !r.enabled) return 'off';
    const parts = [`${formatNumber(r.limit, 3)} ${d.unit}`];
    if (d.over && r.over_m !== null) parts.push(`over ${formatNumber(r.over_m)} m`);
    if (d.window && r.window_min !== null) parts.push(`${formatNumber(r.window_min)} min`);
    if (r.sensor) parts.push(`${r.sensor} sensor`);
    return parts.join(' ');
  };
  for (const d of QC_RULE_DEFS) {
    const a = show(qcRule(before, d.key), d);
    const b = show(qcRule(after, d.key), d);
    if (a !== b) out.push(`${d.label}: ${a} → ${b}`);
  }
  return out;
}

// ---------------------------------------------------------------------- the flight path limits (decision 814)

/** The bounds Teslyn takes for a flight path limit, m (a contract's is 15 m over 1,000 m on F26048). */
export const FLIGHT_PATH_LIMIT_BOUNDS = { allowedM: [1, 500], overM: [1, 100_000] } as const;

/** The flight path's allowed deviation and over distance of a rule list (decision 814), or null when it has none. */
export function flightPathLimits(
  rules: readonly QcRule[],
): { allowedM: number; overM: number | null } | null {
  const r = qcRule(rules, 'flight_path');
  return r ? { allowedM: r.limit, overM: r.over_m } : null;
}

/** Why a typed flight path limit cannot be saved, or null. */
export function flightPathLimitsProblem(allowedM: number | null, overM: number | null): string | null {
  const [a0, a1] = FLIGHT_PATH_LIMIT_BOUNDS.allowedM;
  const [o0, o1] = FLIGHT_PATH_LIMIT_BOUNDS.overM;
  if (allowedM === null || !(allowedM >= a0 && allowedM <= a1))
    return `Enter the flight path's allowed deviation in metres, from ${a0} to ${a1}.`;
  if (overM === null || !(overM >= o0 && overM <= o1))
    return `Enter the flight path's over distance in metres, from ${o0} to ${formatNumber(o1)}.`;
  return null;
}

/**
 * Decision 814: the project's QC limits with the flight path's allowed deviation and over distance set (and the rule
 * on); every other rule as it was. The Flight data QC run (`QCPATH.ALLDEV`, `DIST`, the "over" length of a breach)
 * and the XTQC map read them from here.
 */
export function withFlightPathLimits(rules: readonly QcRule[], allowedM: number, overM: number): QcRule[] {
  const set = (r: QcRule): QcRule => ({ ...r, enabled: true, limit: allowedM, over_m: overM });
  const has = rules.some((r) => r.key === 'flight_path');
  return has
    ? rules.map((r) => (r.key === 'flight_path' ? set(r) : { ...r }))
    : [
        set({ key: 'flight_path', enabled: true, limit: 0, over_m: null, window_min: null, sensor: null }),
        ...rules,
      ];
}

// ------------------------------------------------------------------ the height specs (decision 822)

/** The limits of a distance rule (flight path, terrain clearance): its allowed deviation and over distance. */
export type DistanceRuleKey = 'flight_path' | 'terrain_clearance';

const RULE_WORDS: Readonly<Record<DistanceRuleKey, string>> = {
  flight_path: "the flight path's",
  terrain_clearance: "the terrain clearance's",
};

/** A distance rule's allowed deviation and over distance (decision 822: terrain clearance as flight path), or null. */
export function distanceRuleLimits(
  rules: readonly QcRule[],
  key: DistanceRuleKey,
): { allowedM: number; overM: number | null } | null {
  const r = qcRule(rules, key);
  return r ? { allowedM: r.limit, overM: r.over_m } : null;
}

/** Why a typed distance rule limit cannot be saved, or null (the flight path's bounds for both). */
export function distanceRuleLimitsProblem(
  key: DistanceRuleKey,
  allowedM: number | null,
  overM: number | null,
): string | null {
  const [a0, a1] = FLIGHT_PATH_LIMIT_BOUNDS.allowedM;
  const [o0, o1] = FLIGHT_PATH_LIMIT_BOUNDS.overM;
  const w = RULE_WORDS[key];
  if (allowedM === null || !(allowedM >= a0 && allowedM <= a1))
    return `Enter ${w} allowed deviation in metres, from ${a0} to ${a1}.`;
  if (overM === null || !(overM >= o0 && overM <= o1))
    return `Enter ${w} over distance in metres, from ${o0} to ${formatNumber(o1)}.`;
  return null;
}

/** The project's QC limits with a distance rule's allowed deviation and over distance set (and the rule on). */
export function withDistanceRuleLimits(
  rules: readonly QcRule[],
  key: DistanceRuleKey,
  allowedM: number,
  overM: number,
): QcRule[] {
  const set = (r: QcRule): QcRule => ({ ...r, enabled: true, limit: allowedM, over_m: overM });
  return rules.some((r) => r.key === key)
    ? rules.map((r) => (r.key === key ? set(r) : { ...r }))
    : [...rules, set({ key, enabled: true, limit: 0, over_m: null, window_min: null, sensor: null })];
}

/**
 * The crew's heights (decision 181): a hard deck at or above the lower working height is most likely the two swapped
 * (the C208 script 2.1 has lower working height 20 m, hard deck 15 m). A warning, not a refusal: the crew decides.
 */
export function crewHeightsWarning(hardDeckM: number | null, lowerWorkingM: number | null): string | null {
  if (hardDeckM === null || lowerWorkingM === null || hardDeckM < lowerWorkingM) return null;
  return `The hard deck (${formatNumber(hardDeckM)} m) is not below the lower working height (${formatNumber(lowerWorkingM)} m): are they the right way round? The hard deck is the lower one (script 2.1: lower working height 20 m, hard deck 15 m).`;
}
